import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { 
    Calendar, Clock, Music, Users, Heart, Zap, Radio, 
    TrendingUp, Star, MapPin, Headphones, Mic2, Volume2,
    Activity, Wifi, Signal, Settings, Play, Eye, Upload, Video, Edit3
} from 'lucide-react';
import api from '../services/api';
import SongsStream from '../components/SongsStream';
import VideosStream from '../components/VideosStream';
import ImagesStream from '../components/ImagesStream';
import UpcomingEvents from '../components/UpcomingEvents';
import FeaturedSongs from '../components/FeaturedSongs';
import RecentShows from '../components/RecentShows';
import LatestDeepWaveSongs from '../components/LatestDeepWaveSongs';
import LatestDeepWaveVideos from '../components/LatestDeepWaveVideos';
import LatestDeepWaveImages from '../components/LatestDeepWaveImages';
import TopDeepWaveTracks from '../components/TopDeepWaveTracks';
import TopDeepWaveImages from '../components/TopDeepWaveImages';
import DeepWaveCrew from '../components/DeepWaveCrew';
import BandMessage from '../components/BandMessage';
import HighlightedArtists from '../components/HighlightedArtists';
import PageHeader from '../components/PageHeader';

const Dashboard = () => {
    const { user } = useAuth();
    const [currentTime, setCurrentTime] = useState(new Date());
    const [nextEvent, setNextEvent] = useState(null);
    const [countdown, setCountdown] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
    });
    const [waveStats, setWaveStats] = useState({
        totalPlays: 0,
        totalListeners: 0,
        totalCrew: 0,
        totalArtists: 0,
        totalPageViews: 0,
        totalMediaViews: 0,
        totalUploads: 0
    });
    const [loadingStats, setLoadingStats] = useState(true);
    const [userBadges, setUserBadges] = useState([]);
    const [loadingBadges, setLoadingBadges] = useState(true);
    const [siteIcon, setSiteIcon] = useState(null);

    // Fetch real analytics data
    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                setLoadingStats(true);
                
                // Fetch all stats in parallel
                const [playsRes, listenersRes, pageViewsRes, mediaViewsRes, crewRes, artistsRes, uploadsRes] =
                    await Promise.allSettled([
                        api.get('/tracking/stats/total-plays'),
                        api.get('/tracking/stats/total-listeners'),
                        api.get('/tracking/stats/total-views'),
                        api.get('/tracking/stats/total-media-views'),
                        api.get('/tracking/stats/total-crew'),
                        api.get('/tracking/stats/total-artists'),
                        api.get('/tracking/stats/total-uploads'),
                    ]);

                setWaveStats({
                    totalPlays:      playsRes.status === 'fulfilled'      ? (playsRes.value.data?.totalPlays || 0) : 0,
                    totalListeners:  listenersRes.status === 'fulfilled'  ? (listenersRes.value.data?.totalListeners || 0) : 0,
                    totalPageViews:  pageViewsRes.status === 'fulfilled'  ? (pageViewsRes.value.data?.totalViews || 0) : 0,
                    totalMediaViews: mediaViewsRes.status === 'fulfilled' ? (mediaViewsRes.value.data?.totalMediaViews || 0) : 0,
                    totalCrew:       crewRes.status === 'fulfilled'       ? (crewRes.value.data?.count || 0) : 0,
                    totalArtists:    artistsRes.status === 'fulfilled'    ? (artistsRes.value.data?.count || 0) : 0,
                    totalUploads:    uploadsRes.status === 'fulfilled'    ? (uploadsRes.value.data?.count || 0) : 0,
                });
            } catch (error) {
                console.error('Error fetching analytics:', error);
                // Fallback to basic data if API fails
                setWaveStats({
                    totalPlays: 0,
                    totalListeners: 0,
                    totalArtists: 0,
                    totalViews: 0,
                    totalUsers: 0,
                    totalUploads: 0
                });
            } finally {
                setLoadingStats(false);
            }
        };

        fetchAnalytics();
        fetchUserBadges();
    }, []);

    // Fetch user badges
    const fetchUserBadges = async () => {
        try {
            setLoadingBadges(true);
            const response = await api.get('/badges/my-badges');
            if (response.data.success) {
                setUserBadges(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching user badges:', error);
        } finally {
            setLoadingBadges(false);
        }
    };

    // Fetch next upcoming event
    useEffect(() => {
        const fetchNextEvent = async () => {
            try {
                const response = await api.get('/events/upcoming?limit=1');
                if (response.data.success && response.data.data?.length > 0) {
                    const event = response.data.data[0];
                    // Check if event is actually in the future
                    const eventTime = new Date(event.start_date).getTime();
                    const now = new Date().getTime();
                    
                    if (eventTime > now) {
                        setNextEvent(event);
                    } else {
                        // If no future events, create a placeholder
                        const futureDate = new Date();
                        futureDate.setDate(futureDate.getDate() + 30); // 30 days from now
                        setNextEvent({
                            title: 'Deep Wave Community Showcase',
                            start_date: futureDate.toISOString()
                        });
                    }
                } else {
                    // If no events at all, create a placeholder
                    const futureDate = new Date();
                    futureDate.setDate(futureDate.getDate() + 30); // 30 days from now
                    setNextEvent({
                        title: 'Deep Wave Community Showcase',
                        start_date: futureDate.toISOString()
                    });
                }
            } catch (error) {
                console.error('Error fetching next event:', error);
                // Fallback event in case of API error
                const futureDate = new Date();
                futureDate.setDate(futureDate.getDate() + 30);
                setNextEvent({
                    title: 'Deep Wave Community Showcase',
                    start_date: futureDate.toISOString()
                });
            }
        };

        fetchNextEvent();
    }, []);

    // Fetch site icon from public endpoint
    useEffect(() => {
        const fetchSiteIcon = async () => {
            try {
                const response = await api.get('/admin/settings/public');
                if (response.data.success) {
                    const iconUrl = response.data.data.site_icon;
                    if (iconUrl) {
                        setSiteIcon(iconUrl);
                    }
                }
            } catch (error) {
                console.error('Error fetching site icon:', error);
            }
        };

        fetchSiteIcon();
    }, []);

    // Update countdown every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
            
            if (nextEvent?.start_date) {
                const now = new Date().getTime();
                const eventTime = new Date(nextEvent.start_date).getTime();
                const difference = eventTime - now;

                if (difference > 0) {
                    setCountdown({
                        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
                        seconds: Math.floor((difference % (1000 * 60)) / 1000)
                    });
                } else {
                    setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                }
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [nextEvent]);


    // Mock data for remaining features

    const bestFriends = [
        { id: 1, name: "Luna Martinez", role: "Sound Engineer", avatar: "🎧", status: "online" },
        { id: 2, name: "Kai Thompson", role: "Visual Artist", avatar: "🎨", status: "online" },
        { id: 3, name: "Nova Chen", role: "Photographer", avatar: "📸", status: "away" },
        { id: 4, name: "Echo Williams", role: "Producer", avatar: "🎛️", status: "online" },
    ];



    return (
        <div className="min-h-screen bg-gray-900">
            {/* Page Header with Background Image */}
            <PageHeader
                title="The Ensemble Hub"
                subtitle={`Welcome back to The Deep Wave Arts & Sound, ${user?.profile?.display_name || user?.profile?.username || 'Artist'}`}
                pageType="dashboard"
                height="h-80"
            />

            {/* Ambient Background Effects */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
                <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-700/5 rounded-full blur-3xl animate-pulse delay-2000"></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 -mt-16">
                {/* Command Center Header */}
                <div className="mb-8">
                    <div className="glass-card p-4 sm:p-6 border border-blue-600/30">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-4">
                            <div className="flex-1">
                                <h1 className="text-2xl sm:text-3xl font-black gradient-text flex items-center gap-3 sm:gap-4 mb-2">
                                    {siteIcon ? (
                                        <div className="w-14 h-14 sm:w-20 sm:h-20 flex-shrink-0">
                                            <img
                                                src={siteIcon}
                                                alt="The Deep Wave Icon"
                                                width="80"
                                                height="80"
                                                className="w-full h-full object-cover rounded-xl"
                                            />
                                        </div>
                                    ) : (
                                        <div className="text-3xl sm:text-5xl">🌊</div>
                                    )}
                                    <span className="truncate">The Deep Wave Hub</span>
                                </h1>
                                <p className="text-sm sm:text-base text-gray-400 pr-4">
                                    Welcome back to The Deep Wave, {user?.profile?.display_name || user?.profile?.username}
                                </p>
                            </div>
                            <div className="lg:text-right flex-shrink-0">
                                {nextEvent ? (
                                    <div className="bg-gray-800/50 rounded-lg p-3 sm:p-4 border border-yellow-500/20">
                                        <div className="text-sm sm:text-base font-mono text-blue-400 mb-1 truncate">
                                            <span className="hidden sm:inline">Next Event: </span>{nextEvent.title}
                                        </div>
                                        <div className="text-xl sm:text-2xl font-bold text-yellow-400 mb-1 font-mono">
                                            {countdown.days > 0 && `${countdown.days}d `}
                                            {String(countdown.hours).padStart(2, '0')}:
                                            {String(countdown.minutes).padStart(2, '0')}:
                                            {String(countdown.seconds).padStart(2, '0')}
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-400">
                                            {new Date(nextEvent.start_date).toLocaleDateString('en-US', { 
                                                weekday: 'short',
                                                month: 'short', 
                                                day: 'numeric',
                                                year: window.innerWidth < 640 ? undefined : 'numeric',
                                                hour: 'numeric',
                                                minute: '2-digit'
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-gray-800/50 rounded-lg p-3 sm:p-4 border border-blue-500/20">
                                        <div className="text-lg sm:text-xl font-mono text-blue-400">
                                            {currentTime.toLocaleTimeString()}
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-400">
                                            No upcoming events
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>

                {/* Main Dashboard Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    {/* Wave Statistics */}
                    <div className="glass-card p-6 border border-blue-500/30">
                        <div className="flex items-center gap-3 mb-4">
                            <TrendingUp className="w-6 h-6 text-blue-400" />
                            <h2 className="text-xl font-bold text-white">Wave Statistics</h2>
                        </div>
                        
                        {loadingStats ? (
                            <div className="space-y-4 animate-pulse">
                                <div className="flex justify-between items-center">
                                    <div className="h-4 bg-gray-700 rounded w-24"></div>
                                    <div className="h-6 bg-gray-700 rounded w-16"></div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="h-4 bg-gray-700 rounded w-28"></div>
                                    <div className="h-5 bg-gray-700 rounded w-14"></div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="h-4 bg-gray-700 rounded w-20"></div>
                                    <div className="h-5 bg-gray-700 rounded w-12"></div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="h-4 bg-gray-700 rounded w-26"></div>
                                    <div className="h-5 bg-gray-700 rounded w-16"></div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="h-4 bg-gray-700 rounded w-22"></div>
                                    <div className="h-5 bg-gray-700 rounded w-14"></div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-400">Total Plays</span>
                                    <span className="text-2xl font-bold text-blue-400">{waveStats.totalPlays.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-400">Total Listeners</span>
                                    <span className="text-lg font-bold text-blue-400">{waveStats.totalListeners.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <Eye className="w-4 h-4 text-green-400" />
                                        <span className="text-gray-400">Total Page Views</span>
                                    </div>
                                    <span className="text-lg font-bold text-green-400">{waveStats.totalPageViews.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <Eye className="w-4 h-4 text-cyan-400" />
                                        <span className="text-gray-400">Total Media Views</span>
                                    </div>
                                    <span className="text-lg font-bold text-cyan-400">{waveStats.totalMediaViews.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-purple-400" />
                                        <span className="text-gray-400">Total Crew</span>
                                    </div>
                                    <span className="text-lg font-bold text-purple-400">{waveStats.totalCrew.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <Star className="w-4 h-4 text-yellow-400" />
                                        <span className="text-gray-400">Total Signups</span>
                                    </div>
                                    <span className="text-lg font-bold text-yellow-400">{waveStats.totalArtists.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <Upload className="w-4 h-4 text-blue-500" />
                                        <span className="text-gray-400">Total Uploads</span>
                                    </div>
                                    <span className="text-lg font-bold text-blue-500">{waveStats.totalUploads.toLocaleString()}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* My Badges */}
                    <div className="glass-card p-6 border border-blue-600/30">
                        <div className="flex items-center gap-3 mb-4">
                            <Star className="w-6 h-6 text-yellow-400" />
                            <h2 className="text-xl font-bold text-white">My Badges</h2>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="text-sm text-gray-400 mb-3">
                                Collect badges by engaging with The Deep Wave community
                            </div>
                            
                            {loadingBadges ? (
                                <div className="text-center py-8">
                                    <div className="loading-spinner w-6 h-6 mx-auto mb-2"></div>
                                    <div className="text-sm text-gray-400">Loading badges...</div>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 gap-3">
                                        {userBadges.length > 0 ? (
                                            userBadges.slice(0, 4).map(badge => {
                                                const colorSchemes = {
                                                    purple: 'from-purple-600/20 to-blue-600/20 border-purple-500/30',
                                                    blue: 'from-blue-600/20 to-cyan-600/20 border-blue-500/30',
                                                    green: 'from-green-600/20 to-emerald-600/20 border-green-500/30',
                                                    yellow: 'from-yellow-600/20 to-orange-600/20 border-yellow-500/30',
                                                    pink: 'from-pink-600/20 to-rose-600/20 border-pink-500/30',
                                                    gold: 'from-yellow-500/20 to-amber-600/20 border-yellow-400/30'
                                                };
                                                const colorClass = colorSchemes[badge.color_scheme] || colorSchemes.purple;
                                                const textColor = {
                                                    purple: 'text-purple-300',
                                                    blue: 'text-blue-300',
                                                    green: 'text-green-300',
                                                    yellow: 'text-yellow-300',
                                                    pink: 'text-pink-300',
                                                    gold: 'text-yellow-300'
                                                };
                                                const badgeTextColor = textColor[badge.color_scheme] || textColor.purple;
                                                
                                                // Map icon names to emojis
                                                const iconMap = {
                                                    wave: '🌊',
                                                    edit: '📝',
                                                    calendar: '🎪',
                                                    users: '👥',
                                                    music: '🎵',
                                                    verified: '✅',
                                                    headphones: '🎧',
                                                    heart: '🦋',
                                                    mic: '🎤',
                                                    star: '🏆'
                                                };
                                                const displayIcon = iconMap[badge.icon] || '🏅';
                                                
                                                return (
                                                    <div key={badge.id} className={`bg-gradient-to-br ${colorClass} border rounded-lg p-3 flex flex-col items-center text-center`}>
                                                        <div className="text-2xl mb-1">{displayIcon}</div>
                                                        <div className={`text-sm font-semibold ${badgeTextColor}`}>{badge.name}</div>
                                                        <div className="text-xs text-gray-400">{badge.description}</div>
                                                        {badge.awarded_at && (
                                                            <div className="text-xs text-gray-500 mt-1">
                                                                {new Date(badge.awarded_at).toLocaleDateString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <>
                                                {/* Placeholder badges if none earned yet */}
                                                <div className="bg-gray-800/30 border border-gray-600/30 rounded-lg p-3 flex flex-col items-center text-center opacity-50">
                                                    <div className="text-2xl mb-1">🌊</div>
                                                    <div className="text-sm font-semibold text-gray-500">Welcome</div>
                                                    <div className="text-xs text-gray-500">Coming soon</div>
                                                </div>
                                                <div className="bg-gray-800/30 border border-gray-600/30 rounded-lg p-3 flex flex-col items-center text-center opacity-50">
                                                    <div className="text-2xl mb-1">📝</div>
                                                    <div className="text-sm font-semibold text-gray-500">First Post</div>
                                                    <div className="text-xs text-gray-500">Share content</div>
                                                </div>
                                                <div className="bg-gray-800/30 border border-gray-600/30 rounded-lg p-3 flex flex-col items-center text-center opacity-50">
                                                    <div className="text-2xl mb-1">🎪</div>
                                                    <div className="text-sm font-semibold text-gray-500">Event Goer</div>
                                                    <div className="text-xs text-gray-500">Attend event</div>
                                                </div>
                                                <div className="bg-gray-800/30 border border-gray-600/30 rounded-lg p-3 flex flex-col items-center text-center opacity-50">
                                                    <div className="text-2xl mb-1">👥</div>
                                                    <div className="text-sm font-semibold text-gray-500">Community</div>
                                                    <div className="text-xs text-gray-500">30 days active</div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    
                                    <div className="text-center pt-2">
                                        <div className="text-lg text-blue-400 font-semibold">
                                            {userBadges.length} / {userBadges.length + 4}
                                        </div>
                                        <div className="text-xs text-gray-400">Badges Earned</div>
                                        {userBadges.length > 4 && (
                                            <div className="text-xs text-blue-400 mt-1">
                                                +{userBadges.length - 4} more badges
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="glass-card p-6 border border-blue-700/30">
                        <div className="flex items-center gap-3 mb-4">
                            <Zap className="w-6 h-6 text-blue-500" />
                            <h2 className="text-xl font-bold text-white">Quick Actions</h2>
                        </div>
                        
                        <div className="space-y-3">
                            <Link 
                                to={`/profile/${user?.profile?.username}#posts`}
                                className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center gap-3"
                            >
                                <Edit3 className="w-5 h-5" />
                                Create Post
                            </Link>
                            <Link 
                                to={`/profile/${user?.profile?.username}#images`}
                                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center gap-3"
                            >
                                <Upload className="w-5 h-5" />
                                Upload Photos
                            </Link>
                            <Link 
                                to={`/profile/${user?.profile?.username}#videos`}
                                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center gap-3"
                            >
                                <Video className="w-5 h-5" />
                                Upload Video
                            </Link>
                            <Link 
                                to={`/profile/${user?.profile?.username}#music`}
                                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center gap-3"
                            >
                                <Music className="w-5 h-5" />
                                Upload Audio
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Top Section - Featured Songs & Upcoming Events */}
                <div className="mb-4 sm:mb-8 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-yellow-500/30">
                        <FeaturedSongs limit={3} showHeader={true} className="" />
                    </div>
                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-yellow-500/30">
                        <UpcomingEvents limit={3} showHeader={true} className="" />
                    </div>
                </div>

                {/* Highlighted Artists Section */}
                <section className="py-10 sm:py-16 bg-gradient-to-b from-gray-900/50 to-gray-900/30 -mx-4 sm:-mx-6 mb-6 sm:mb-8">
                    <div className="px-4 sm:px-6 max-w-7xl mx-auto">
                        <div className="text-center mb-8 sm:mb-16">
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black gradient-text mb-4 sm:mb-6">
                                Featured Artists
                            </h2>
                            <p className="text-base sm:text-lg text-gray-400 max-w-3xl mx-auto">
                                Discover the remarkable artists currently highlighted in The Deep Wave ensemble.
                                These featured creators are pushing boundaries, exploring new artistic territories, and inspiring our community.
                            </p>
                        </div>
                        <HighlightedArtists limit={6} showTitle={false} className="border border-yellow-500/30" />
                    </div>

                    <BandMessage />
                </section>

                {/* Secondary Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-blue-500/30">
                        <TopDeepWaveTracks limit={5} viewAllPosition="bottom" />
                    </div>
                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-cyan-500/30">
                        <TopDeepWaveImages limit={8} viewAllPosition="bottom" />
                    </div>
                </div>

                {/* Official Deep Wave Content + Community Streams */}
                <div className="mt-4 sm:mt-8 space-y-4 sm:space-y-8">
                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-blue-600/30">
                        <LatestDeepWaveSongs limit={6} className="" viewAllPosition="bottom" />
                    </div>

                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-pink-500/30">
                        <LatestDeepWaveVideos limit={6} className="" viewAllPosition="bottom" />
                    </div>

                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-cyan-500/30">
                        <LatestDeepWaveImages limit={6} className="" viewAllPosition="bottom" />
                    </div>

                    {/* Community Content Section */}
                    <div className="text-center py-4 sm:py-8">
                        <h2 className="text-3xl sm:text-5xl md:text-6xl font-black gradient-text mb-4 sm:mb-6">
                            Community Creations
                        </h2>
                        <p className="text-base sm:text-xl text-gray-400 max-w-3xl mx-auto">
                            Here are the latest creative works shared by The Deep Wave community members and guest artists.
                            Join to experience the communities artistic content and connect with other talented creators.
                        </p>
                    </div>

                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-blue-500/30">
                        <SongsStream limit={6} className="" viewAllPosition="bottom" />
                    </div>

                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-green-500/30">
                        <VideosStream limit={6} className="" viewAllPosition="bottom" />
                    </div>

                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-cyan-500/30">
                        <ImagesStream limit={6} className="" viewAllPosition="bottom" />
                    </div>

                    {/* Events Section */}
                    <div className="mt-4 sm:mt-8">
                        <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-3">
                            <Calendar className="w-6 h-6 sm:w-7 sm:h-7 text-yellow-400" />
                            Events & Shows
                        </h2>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                            <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-green-500/30">
                                <RecentShows limit={5} />
                            </div>
                            <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-yellow-500/30">
                                <UpcomingEvents limit={5} />
                            </div>
                        </div>
                    </div>

                    {/* Deep Wave Crew Section */}
                    <section className="py-10 sm:py-16 bg-gradient-to-b from-gray-900/30 to-gray-900/50 -mx-4 sm:-mx-6 mt-4 sm:mt-8">
                        <div className="px-4 sm:px-6 max-w-7xl mx-auto">
                            <div className="text-center mb-8 sm:mb-16">
                                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black gradient-text mb-4 sm:mb-6">
                                    The Deep Wave Crew
                                </h2>
                                <p className="text-base sm:text-lg text-gray-400 max-w-3xl mx-auto">
                                    Meet the passionate team behind The Deep Wave ensemble. Our crew members are dedicated artists,
                                    curators, and creative professionals who work together to foster an inclusive and inspiring community.
                                </p>
                            </div>
                            <DeepWaveCrew showTitle={false} />
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;